/**
 * <pdp-dispatch-countdown>
 * Ticks down to the daily dispatch cutoff in IST (Asia/Kolkata).
 * After cutoff it swaps in the after-cutoff template and keeps counting
 * down to tomorrow's cutoff.
 *
 * Expects data attributes:
 *   data-cutoff-hour       IST hour (0–23)
 *   data-cutoff-minute     IST minute (0–59)
 *   data-template-before   "Order in {time} for today's dispatch"
 *   data-template-after    "Ships tomorrow. Order now ..."
 *
 * Updates [data-time] and swaps the surrounding text template.
 */
if (!customElements.get('pdp-dispatch-countdown')) {
  customElements.define(
    'pdp-dispatch-countdown',
    class PdpDispatchCountdown extends HTMLElement {
      constructor() {
        super();
        this.timeEl = this.querySelector('[data-time]');
        this.textEl = this.querySelector('.pdp-dispatch-countdown__text');
        this.cutoffHour = Number(this.dataset.cutoffHour);
        if (!Number.isFinite(this.cutoffHour)) this.cutoffHour = 16;
        this.cutoffMinute = Number(this.dataset.cutoffMinute) || 0;
        this.templateBefore = this.dataset.templateBefore || "Order in {time} for today's dispatch";
        this.templateAfter = this.dataset.templateAfter || 'Ships tomorrow. Order now to lock in dispatch';
        this.tickTimeout = null;
      }

      connectedCallback() {
        this.tick();
      }

      disconnectedCallback() {
        if (this.tickTimeout !== null) {
          window.clearTimeout(this.tickTimeout);
          this.tickTimeout = null;
        }
      }

      tick() {
        const remaining = this.remainingMs();
        if (remaining.isAfterCutoff) {
          this.renderAfterCutoff(remaining.ms);
        } else {
          this.renderBeforeCutoff(remaining.ms);
        }
        const msUntilNextSecond = 1000 - (Date.now() % 1000);
        this.tickTimeout = window.setTimeout(() => this.tick(), msUntilNextSecond);
      }

      remainingMs() {
        const nowIst = this.toIstParts(new Date());
        const cutoffToday = this.cutoffTimestampFor(nowIst, 0);
        const cutoffTomorrow = this.cutoffTimestampFor(nowIst, 1);
        const nowTs = this.istTimestamp(nowIst);
        if (nowTs < cutoffToday) {
          return { ms: cutoffToday - nowTs, isAfterCutoff: false };
        }
        return { ms: cutoffTomorrow - nowTs, isAfterCutoff: true };
      }

      toIstParts(date) {
        const formatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
        const parts = formatter.formatToParts(date).reduce((acc, part) => {
          if (part.type !== 'literal') acc[part.type] = Number(part.value);
          return acc;
        }, {});
        return parts;
      }

      istTimestamp(parts) {
        return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      }

      cutoffTimestampFor(parts, dayOffset) {
        return Date.UTC(parts.year, parts.month - 1, parts.day + dayOffset, this.cutoffHour, this.cutoffMinute, 0);
      }

      formatTime(ms) {
        const totalSeconds = Math.max(0, Math.floor(ms / 1000));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (n) => String(n).padStart(2, '0');
        return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
      }

      renderBeforeCutoff(ms) {
        if (this.timeEl) this.timeEl.textContent = this.formatTime(ms);
        this.applyTemplate(this.templateBefore, true);
      }

      renderAfterCutoff(ms) {
        if (this.timeEl) this.timeEl.textContent = this.formatTime(ms);
        this.applyTemplate(this.templateAfter, false);
      }

      applyTemplate(template, showTime) {
        if (!this.textEl || !this.timeEl) return;
        if (!template.includes('{time}')) {
          this.textEl.textContent = template;
          return;
        }
        const parts = template.split('{time}');
        this.textEl.textContent = '';
        if (parts[0]) this.textEl.appendChild(document.createTextNode(parts[0]));
        this.textEl.appendChild(this.timeEl);
        if (parts[1]) this.textEl.appendChild(document.createTextNode(parts[1]));
      }
    }
  );
}
